import { useEffect, useRef, useState } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

interface RequestLog {
  id: string
  started_at: string
  finished_at: string
  direction: string
  provider: string
  application_id: number | null
  correlation_id: string | null
  method: string
  url: string
  http_status: number | null
  business_code: string | null
  duration_ms: number
  error_type: string | null
  exchange?: { request: unknown; response: unknown }
}

function localDate(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 19)
}

function initialFilters() {
  return { from: localDate(new Date(Date.now() - 86400000)), to: localDate(new Date(Date.now() + 1000)),
    direction: '', provider: '', correlation_id: '', business_code: '', http_status: '', application_id: '' }
}

type Filters = ReturnType<typeof initialFilters>

export default function RequestLogsPage() {
  const [draft, setDraft] = useState(initialFilters)
  const [filters, setFilters] = useState(initialFilters)
  const [page, setPage] = useState(1)
  const [revision, setRevision] = useState(0)
  const [rows, setRows] = useState<RequestLog[]>([])
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<RequestLog | null>(null)
  const [detailError, setDetailError] = useState('')
  const detailGeneration = useRef(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setRows([])
    setTotal(0)
    setPages(0)
    const params: Record<string, string | number> = { page, per_page: 25 }
    for (const [key, value] of Object.entries(filters)) {
      if (value) params[key] = key === 'from' || key === 'to' ? new Date(value).toISOString() : value
    }
    api.get<RequestLog[]>('/request-logs', params)
      .then(result => {
        if (!active) return
        setRows(result.data)
        setTotal(result.pagination?.total ?? 0)
        setPages(result.pagination?.pages ?? 0)
      })
      .catch((err: Error) => { if (active) setError(err.message || 'Unable to load request logs') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [filters, page, revision])

  useEffect(() => () => { detailGeneration.current++ }, [])

  async function openDetail(id: string) {
    const generation = ++detailGeneration.current
    setSelected(id)
    setDetail(null)
    setDetailError('')
    try {
      const result = await api.get<RequestLog>(`/request-logs/${encodeURIComponent(id)}`)
      if (detailGeneration.current === generation) setDetail(result.data)
    } catch (err) {
      if (detailGeneration.current === generation) setDetailError(err instanceof Error ? err.message : 'Unable to load details')
    }
  }

  function download() {
    if (!detail) return
    const blob = new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `request-log-${detail.id}.json`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  function input(key: keyof Filters, label: string, type = 'text') {
    return <label className="space-y-1 text-sm" key={key}>
      <span className="text-muted-foreground">{label}</span>
      <Input type={type} step={type === 'datetime-local' ? 1 : undefined} value={draft[key]} required={type === 'datetime-local'}
        onChange={event => setDraft(current => ({ ...current, [key]: event.target.value }))} />
    </label>
  }

  return <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-semibold">Request Logs</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Outbound requests to payment providers and business webhook receivers. HTTP status and provider business code are separate results.
      </p>
    </div>
    <form className="grid gap-4 rounded-lg border bg-card p-4 md:grid-cols-4" onSubmit={event => {
      event.preventDefault()
      const start = new Date(draft.from).getTime(), end = new Date(draft.to).getTime()
      if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || end - start > 31 * 86400000) {
        setError('Choose a valid time range of at most 31 days.'); return
      }
      setPage(1); setFilters({ ...draft }); setRevision(value => value + 1)
    }}>
      {input('from', 'From (local time)', 'datetime-local')}
      {input('to', 'To (local time)', 'datetime-local')}
      <label className="space-y-1 text-sm"><span className="text-muted-foreground">Destination</span>
        <select className="flex h-10 w-full rounded-md border bg-background px-3" value={draft.direction}
          onChange={event => setDraft(current => ({ ...current, direction: event.target.value }))}>
          <option value="">All destinations</option><option value="provider">Payment provider</option><option value="business">Business webhook</option>
        </select>
      </label>
      {input('provider', 'Provider (e.g. photonpay)')}
      {input('correlation_id', 'Request / event / order ID (exact)')}
      {input('business_code', 'Business code (e.g. VCC3020)')}
      {input('http_status', 'HTTP status', 'number')}
      {input('application_id', 'Application ID', 'number')}
      <div className="flex items-center gap-3 md:col-span-4">
        <Button type="submit" disabled={loading}>Search</Button>
        <Button type="button" variant="outline" disabled={loading} onClick={() => {
          const next = initialFilters(); setDraft(next); setFilters(next); setPage(1); setRevision(value => value + 1)
        }}>Last 24 hours</Button>
        <span className="text-xs text-muted-foreground">Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
      </div>
    </form>
    <p className="text-xs text-muted-foreground">
      Records appear asynchronously and may be lost during overload or abrupt shutdown. Use Last 24 hours to refresh the time window. Older container logs are not imported. Secrets and personal data are redacted. Files, streams and oversized bodies show metadata only. Application may be unknown for shared provider calls.
    </p>
    {error && <div role="alert" className="rounded-md border border-destructive p-4 text-destructive">{error}</div>}
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted"><tr>
          {['Time (local)', 'Destination', 'Request', 'HTTP / business code', 'Duration', ''].map((title, index) => <th key={index} className="p-3 font-medium">{title}</th>)}
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={6} className="p-8 text-center" role="status">Loading request logs…</td></tr>}
          {!loading && !error && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No recorded requests match these filters.</td></tr>}
          {!loading && rows.map(row => <tr key={row.id} className="border-t align-top">
            <td className="whitespace-nowrap p-3">{new Date(row.started_at).toLocaleString()}<div className="text-xs text-muted-foreground">App: {row.application_id ?? 'Unknown'}</div></td>
            <td className="p-3">{row.provider}<div className="text-xs text-muted-foreground">{row.direction}</div></td>
            <td className="max-w-sm break-all p-3"><span className="font-medium">{row.method}</span> {row.url}<div className="mt-1 font-mono text-xs text-muted-foreground">{row.correlation_id ?? 'No correlation ID'}</div></td>
            <td className="p-3"><span>{row.http_status ?? 'No response'}</span><div className="font-mono text-xs">{row.business_code ?? '—'}</div>{row.error_type && <div className="text-destructive">{row.error_type}</div>}</td>
            <td className="whitespace-nowrap p-3">{row.duration_ms} ms</td>
            <td className="p-3"><Button variant="outline" size="sm" onClick={() => void openDetail(row.id)}>Details</Button></td>
          </tr>)}
        </tbody>
      </table>
    </div>
    <div className="flex items-center justify-between text-sm">
      <span>{total} records · Page {page} of {Math.max(1, pages)}</span>
      <div className="flex gap-2"><Button variant="outline" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>Previous</Button>
        <Button variant="outline" disabled={loading || page >= pages} onClick={() => setPage(value => value + 1)}>Next</Button></div>
    </div>
    <Dialog open={selected !== null} onOpenChange={open => { if (!open) { detailGeneration.current++; setSelected(null) } }}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader><DialogTitle>Request and response</DialogTitle></DialogHeader>
        {detailError ? <p role="alert" className="text-destructive">{detailError}</p> : !detail ? <p role="status">Loading details…</p> : <>
          <div className="flex items-center justify-between gap-3"><span className="text-sm">Sanitized evidence · timestamps in UTC</span><Button onClick={download}>Download JSON</Button></div>
          <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-4 text-xs">{JSON.stringify(detail, null, 2)}</pre>
        </>}
      </DialogContent>
    </Dialog>
  </div>
}
